import TYPES from '@/types';
import Vue from 'vue';

// Application
import GetCustomerContractedInvestmentProductsQuery
  from '@/modules/flagship/customer-contracted-investment-products/application/queries/get-customer-contracted-investment-products-query';
import GetInvestmentProductsQuery
  from '@/modules/flagship/catalogs/investment-products/application/queries/get-investment-products-query';
import CustomerBankAccountChooseYourDocumentsService
  from '@/modules/user/customer-bank-account/application/services/customer-bank-account-choose-your-documents-service';
import { SearchAllianzRequestForMovementsToPolicyQuery }
  from '@/modules/my-investment/allianz/allianz-request-for-movments-to-policy/application/queries';
import {
  GetAllianzAccountQuery,
} from '@/modules/my-investment/allianz/allianz-account/application/queries';

// Domain
import { InvestmentProductsEntity }
  from '@/modules/flagship/catalogs/investment-products/domain/entities/investment-products-entity';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class UpdateBankAccountChooseYourDocumentsViewModel {
  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.GET_CUSTOMER_CONTRACTED_INVESTMENT_PRODUCTS_QUERY)
  private readonly get_contracted_products_query!: GetCustomerContractedInvestmentProductsQuery;

  @Inject(TYPES.GET_INVESTMENT_PRODUCTS_QUERY)
  private readonly get_investment_products_query!: GetInvestmentProductsQuery;

  @Inject(TYPES.SEARCH_ALLIANZ_REQUEST_FOR_MOVEMENTS_TO_POLICY_QUERY)
  private readonly search_request_for_movements!: SearchAllianzRequestForMovementsToPolicyQuery;

  @Inject(TYPES.GET_ALLIANZ_ACCOUNT_QUERY)
  private readonly get_allianz_account_query!: GetAllianzAccountQuery;

  @Inject(TYPES.CUSTOMER_BANK_ACCOUNT_CHOOSE_YOUR_DOCUMENTS_SERVICE)
  private readonly choose_your_documents_service!: CustomerBankAccountChooseYourDocumentsService;

  readonly view!: Vue;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.user-profile-my-documents-tab.update-bank-account.choose-your-document';

  public constructor(view: Vue) {
    this.view = view;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  private readonly customer_id = sessionStorage.getItem('user_id');

  readonly REQUEST_TYPE = 'CHANGE_OF_CLABE_BANKING_FOR_WITHDRAWALS'

  allianz_account_id = ''

  wealth_product_id = ''

  pocket_product_id = ''

  is_a_wealth_change_of_bank_account_request_active = false;

  is_a_pocket_change_of_bank_account_request_active = false;

  has_sowos_wealth_contracted = false

  has_sowos_pocket_contracted = false

  pocket_option_is_checked = false

  wealth_option_is_checked = false

  selected_products_ids: Array<string> = [];

  investment_products: Array<InvestmentProductsEntity> = [];

  initialize = async () => {
    await this.loadContractedProducts();
    await this.loadInvestmentProducts();
    this.loadInformationFromStore();
    await this.loadAllianzAccount();
  }

  get is_begin_btn_disabled() {
    return !(this.pocket_option_is_checked || this.wealth_option_is_checked);
  }

  setInformationToStore = () => {
    this.selected_products_ids = [];
    if (this.pocket_option_is_checked) {
      this.selected_products_ids.push(this.pocket_product_id);
    }
    if (this.wealth_option_is_checked) {
      this.selected_products_ids.push(this.wealth_product_id);
    }
    this.choose_your_documents_service.setChooseYourDocumentsData({
      pocket_option_is_checked: this.pocket_option_is_checked,
      wealth_option_is_checked: this.wealth_option_is_checked,
      selected_investment_product_ids: this.selected_products_ids,
    });
  }

  loadInformationFromStore = () => {
    const information = this.choose_your_documents_service.getChooseYourDocumentsData();
    this.pocket_option_is_checked = information.pocket_option_is_checked;
    this.wealth_option_is_checked = information.wealth_option_is_checked;
    this.selected_products_ids = information.selected_investment_product_ids;
  }

  loadInvestmentProducts = async () => {
    try {
      this.investment_products = await this.get_investment_products_query.execute();
      const wealth_product = this.investment_products.find((item) => item.name === 'sowos_wealth');
      if (wealth_product) this.wealth_product_id = wealth_product.id;
      const pocket_product = this.investment_products.find((item) => item.name === 'sowos_pocket');
      if (pocket_product) this.pocket_product_id = pocket_product.id;
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.get_contracted_products_query'));
    }
  }

  loadContractedProducts = async () => {
    try {
      const { has_sowos_pocket_contracted, has_sowos_wealth_contracted } = await this
        .get_contracted_products_query.execute();
      this.has_sowos_wealth_contracted = has_sowos_wealth_contracted;
      this.has_sowos_pocket_contracted = has_sowos_pocket_contracted;
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.get_contracted_products_query'));
    }
  }

  loadAllianzAccount = async () => {
    try {
      const { id } = await this.get_allianz_account_query.execute(
        { customer_id: this.customer_id },
      );
      if (id) {
        this.allianz_account_id = id;
        if (this.has_sowos_wealth_contracted) {
          await this.loadChangeOfBankAccountRequestActive(this.wealth_product_id);
        }
        if (this.has_sowos_pocket_contracted) {
          await this.loadChangeOfBankAccountRequestActive(this.pocket_product_id);
        }
      } else {
        this.message_notifier.showErrorNotification(this.translate('errors.'));
      }
    } catch (error) {
      if (error.message !== `Error customer_id:${this.customer_id}, not exists`) {
        this.message_notifier.showErrorNotification(this.translate('errors.load_account'));
      }
    }
  }

  loadChangeOfBankAccountRequestActive = async (investment_product_id: string) => {
    try {
      const query_params = {
        contract_id: this.allianz_account_id,
        investment_product_id,
        request_type: this.REQUEST_TYPE,
        is_active: true,
      };
      const pending_request = await this.search_request_for_movements
        .execute(query_params);
      if (pending_request.length) {
        if (investment_product_id === this.wealth_product_id) {
          this.is_a_wealth_change_of_bank_account_request_active = true;
        }
        if (investment_product_id === this.pocket_product_id) {
          this.is_a_pocket_change_of_bank_account_request_active = true;
        }
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.search_request_for_movements'));
    }
  }

  nextStep = () => {
    this.setInformationToStore();
    this.view.$emit('nextStep');
  }

  closeModal = () => {
    this.view.$emit('closeModal');
  }
}
